import { configureStore } from '@reduxjs/toolkit';
import { thunk } from 'redux-thunk';
import apiMiddleware from './apiMiddleware';

const FORCE_REDRAW = 'FORCE_REDRAW';
const SET_USER = 'SET_USER';
const SET_THEME = 'SET_THEME';
const SET_ROUTE = 'SET_ROUTE';
const SET_ACCESS_TOKEN = 'SET_ACCESS_TOKEN';
const SET_USER_ID = 'SET_USER_ID';
const SET_FOLLOWING_DIALOG = 'SET_FOLLOWING_DIALOG';
const FETCH_DATA_SUCCESS = 'FETCH_DATA_SUCCESS';
const FETCH_TAGS_SUCCESS = 'FETCH_TAGS_SUCCESS';
const RESET_STATE_FIELD = 'RESET_STATE_FIELD';
const FETCH_IMAGES_SUCCESS = 'FETCH_IMAGES_SUCCESS';
const CREATE_POST_SUCCESS = 'CREATE_POST_SUCCESS';
const CREATE_POST_FAILURE = 'CREATE_POST_FAILURE';
const CREATE_REACTION_SUCCESS = 'CREATE_REACTION_SUCCESS';
const FETCH_COMMENTS_SUCCESS = 'FETCH_COMMENTS_SUCCESS';
const POST_COMMENT_SUCCESS = 'POST_COMMENT_SUCCESS';
const FETCH_POST_SUCCESS = 'FETCH_POST_SUCCESS';
const FETCH_POST_FAILURE = 'FETCH_POST_FAILURE';
const UPLOAD_IMAGE_SUCCESS = 'UPLOAD_IMAGE_SUCCESS';
const COMMENT_REACTION_SUCCESS = 'COMMENT_REACTION_SUCCESS';
const FETCH_PROFILE_DATA_SUCCESS = 'FETCH_PROFILE_DATA_SUCCESS';
const UPDATE_SETTINGS_SUCCESS = 'UPDATE_SETTINGS_SUCCESS';
const FETCH_PROFILE_SUCCESS = 'FETCH_PROFILE_SUCCESS';
const FETCH_PROFILE_FAILURE = 'FETCH_PROFILE_FAILURE';
const FETCH_PROFILE_TAB_SUCCESS = 'FETCH_PROFILE_TAB_SUCCESS';
const TOGGLE_FAVORITE_SUCCESS = 'TOGGLE_FAVORITE_SUCCESS';
const FETCH_NEXT_PAGE_EVENT = 'FETCH_NEXT_PAGE_EVENT';
const UPLOAD_PROFILE_PICTURE_SUCCESS = 'UPLOAD_PROFILE_PICTURE_SUCCESS';
const SEARCH_SUCCESS = 'SEARCH_SUCCESS';
const ADD_NOTIFICATION = 'ADD_NOTIFICATION';
const HIDE_POST = 'HIDE_POST';
const UPDATE_SETTINGS_FAILURE = 'UPDATE_SETTINGS_FAILURE';
const ASK_TO_LOGIN = 'ASK_TO_LOGIN';
const PLAY_VIDEO = 'PLAY_VIDEO';

export const playVideo = (videoId) => {
  return {
    type: PLAY_VIDEO,
    videoId
  };
};

export const askToLogin = (mandatory = false) => {
  return {
    type: ASK_TO_LOGIN,
    mandatory
  };
};

export const hidePost = (postId) => {
  return {
    type: HIDE_POST,
    postId
  };
};

export const addNotification = (notification) => {
  return {
    type: ADD_NOTIFICATION,
    notification
  };
};

export const fetchNextPageEvent = (nextPageEvent) => {
  return {
    type: FETCH_NEXT_PAGE_EVENT,
    nextPageEvent
  };
};

export const forceRedraw = (date) => {  
  return {
    type: FORCE_REDRAW,
    date
  };
};

export const setUser = (user) => {  
  return {
    type: SET_USER,
    user
  };
};

export const setTheme = (theme) => {
  return {
    type: SET_THEME,
    theme
  };
};

export const setRoute = (route) => {
  return {
    type: SET_ROUTE,
    route
  };
};

export const setAccessToken = (accessToken) => {
  return {
    type: SET_ACCESS_TOKEN,
    accessToken
  };
};

export const setUserId = (userId) => {
  return {
    type: SET_USER_ID,
    userId
  };
};

export const setFollowingDialog = (followingDialog) => {
  return {
    type: SET_FOLLOWING_DIALOG,
    followingDialog
  };
};

export const resetStateField = (field) => {
  return {
    type: RESET_STATE_FIELD,
    field
  };
};

const initialState = {
  // initial state goes here
  forceRedraw: null,
  user: null, //Authenticated user
  theme: 'light',
  route: null,
  accessToken: '',
  userId: null,
  followingDialog: false,
  posts: null,
  tags: null,
  createdPost: null,
  createdReaction: null,
  comments: null,
  createdComment: null,
  post: null,
  uploadedImage: null,
  commentReaction: null,
  newUserCreated: false,
  settingsUpdated: null,
  otherUser: null,
  profileTab: null,
  favorite: null,
  nextPageEvent: null,
  searchResults: null,
  notifications: [],
  removedPost: null,
  settingsUpdateError: null,
  askToLogin: null,
  playVideo: null
};

const rootReducer = (state = initialState, action) => {
  switch (action.type) {
    case PLAY_VIDEO:
      return {
        ...state,
        playVideo: {
          videoId: action.videoId
        }
      };
    case ASK_TO_LOGIN:
      return {
        ...state,
        askToLogin: {
          mandatory: action.mandatory
        }
      };
    case UPDATE_SETTINGS_FAILURE:
      return {
        ...state,
        settingsUpdateError: action.error
      };
    case HIDE_POST:
      return {
        ...state,
        removedPost: action.postId
      };
    case ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [...state.notifications, action.notification]
      };
    case SEARCH_SUCCESS:
      return {
        ...state,
        searchResults: action.payload
      };
    case UPLOAD_PROFILE_PICTURE_SUCCESS:
      return {
        ...state,
        user: action.payload
      };
    case FETCH_NEXT_PAGE_EVENT:
      return {
        ...state,
        nextPageEvent: action.nextPageEvent
      };
    case TOGGLE_FAVORITE_SUCCESS:
      return {
        ...state,
        favorite: action.payload
      };
    case FETCH_PROFILE_TAB_SUCCESS:
      return {
        ...state,
        profileTab: action.payload
      };
    case FETCH_PROFILE_SUCCESS:
      return {
        ...state,
        otherUser: action.payload
      };
    case FETCH_PROFILE_FAILURE:
      return {
        ...state,
        otherUser: false
      };
    case UPDATE_SETTINGS_SUCCESS:
      // Merge new values with existing user object
      return {
        ...state,
        settingsUpdated: (new Date()).getTime(),
        user: {
          ...state.user,
          ...action.payload
        }
      };
    case SET_FOLLOWING_DIALOG:
      return {
        ...state,
        followingDialog: action.followingDialog
      };
    case FETCH_PROFILE_DATA_SUCCESS:
      return {
        ...state,
        user: action?.payload?.user,
        newUserCreated: action?.payload?.newUserCreated
      };
    case FORCE_REDRAW:
      return {
        ...state,
        forceRedraw: Date.now()
      };
    case SET_USER:
      return {
        ...state,
        user: action.user
      };
    case SET_THEME:
      return {
        ...state,
        theme: action.theme
      };
    case SET_ROUTE:
      return {
        ...state,
        route: action.route
      };
    case SET_ACCESS_TOKEN:
      return {
        ...state,
        accessToken: action.accessToken
      };
    case SET_USER_ID:
      return {
        ...state,
        userId: action.userId
      };
    case FETCH_DATA_SUCCESS:
      return {
        ...state,
        posts: action?.payload?.hits
      };
    case FETCH_TAGS_SUCCESS:
      return {
        ...state,
        tags: action?.payload?.tags
      };
    case FETCH_POST_FAILURE:
      return {
        ...state,
        post: { status: 'error', error: action?.payload?.error }
      };
    case FETCH_POST_SUCCESS:
      return {
        ...state,
        post: action?.payload
      };
    case RESET_STATE_FIELD:
      const initialField = initialState[action.field];
      return {
        ...state,
        [action.field]: initialField
      };
    case FETCH_IMAGES_SUCCESS:
      return {
        ...state,
        images: action?.payload
      };
    case CREATE_POST_SUCCESS:
      return {
        ...state,
        createdPost: action?.payload
      };
    case CREATE_POST_FAILURE:
      return {
        ...state,
        createdPost: action.error
      };
    case CREATE_REACTION_SUCCESS:
      return {
        ...state,
        createdReaction: action?.payload
      };
    case FETCH_COMMENTS_SUCCESS:
      return {
        ...state,
        comments: action?.payload
      };
    case POST_COMMENT_SUCCESS:
      return {
        ...state,
        createdComment: action?.payload
      };
    case UPLOAD_IMAGE_SUCCESS:
      return {
        ...state,
        uploadedImage: action?.payload
      };
    case COMMENT_REACTION_SUCCESS:
      return {
        ...state,
        commentReaction: action?.payload
      };
    default:
      return state;
  }
};

const store = configureStore({
  reducer: rootReducer, // replace with your actual root reducer
  middleware: (getDefaultMiddleware) => getDefaultMiddleware({
    serializableCheck: false, // Disable serializable check for file uploads
  }).concat(thunk, apiMiddleware),
});

export default store;